import { Injectable } from '@angular/core';
import { DetailedEntityActionService } from '../../../../shared/services/searchable-entity/detailed-entity/detailed-entity-action.service';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../../shared/shared-service-registry';
import { ReportsConfigurationDataService } from './reports-configuration-data.service';
import { ReportsConfigurationBackendService } from './reports-configuration-backend.service';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchParams } from '../../../../shared/search/models/search-params.model';
import { SearchStatus } from '../../../../shared/search/models/search-status.model';
import { ReportConfigurationSearchService } from './reports-configuration-search.service';

@Injectable()
export class ReportsConfigurationActionService extends DetailedEntityActionService {

  protected dataService: ReportsConfigurationDataService;
  protected backendService: ReportsConfigurationBackendService;

  constructor(protected coreServiceRegistry: CoreServiceRegistry,
    protected sharedServiceRegistry: SharedServiceRegistry) {
    super(coreServiceRegistry, sharedServiceRegistry);
    const searchParams: SearchParams = new SearchParams();
    searchParams.tableId = 'wmreportsearchtd';
    searchParams.isFullFetch = true;
    searchParams.columnCount = 10;
  }

  public editPage() {
    // LFWM-2991-No modification issue-setting originalDataMap
    this.dataService.setOriginalDataMap();
    if (this.dataService.displayMode === 'V') {
      this.dataService.setDisplayMode('E');
      this.buttonService.initializeButtons(this.dataService.primaryDataMap);
    } else if (this.dataService.displayMode === 'E') {
      this.dataService.setDisplayMode('V');
      this.buttonService.initializeButtons(this.dataService.primaryDataMap);
      this.clearEditedFlags();
    }
  }

  /* Method used to delete the selected Line Items
  overriding for - adding detail line items property in moduleProperties itself instead of adding detailItems
  detailItems is empty*/
  public deleteLineItem() {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    const selectedRows = this.dataService.getDetailsSearchService().getSelectedRows();
    if (noOfLineItems === 0) {
      this.errorMsg = 'msg_no_detail';
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return;
    }
    if (selectedRows.length > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const deleteLineItemRequest: any[] = [];
            selectedRows.forEach((row: any) => {
              deleteLineItemRequest.push({
                'moduleProperties': [{ 'cName': 'rowId', 'cVal': row.cells['rowId'].cVal }],
                'screenType': this.dataService.detailsSearchParams.tableId,
                'detailItems': []
              });
            });
            this.backendService.deleteSelectedLineItem(deleteLineItemRequest).subscribe((resp: any) => {
              if (resp['statusCode'] && resp['statusMessage']) {
                if (resp['statusCode'] === 200) {
                  this.alertService.clearAll().success(this.resourceService.get(this.successMsg));
                  this.dataService.reloadDetailTable();
                }
              } else {
                if (resp.error) {
                  this.alertService.clearAll().error(this.resourceService.get(resp.error));
                }
              }
            }, (error: any) => {
              this.alertService.clearAll().error(this.resourceService.get(error));
            });
          }
        });
    } else {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
    }
  }

  /*this method forms the request for saving line items. If the line item is an existing one while sending it for saving,
    the request should contain the original data as well as the edited data.
    The original data will be prefixed with _original.
    If it is a new line item the original prefix need not be appended
   overrriding for - no need to push rowId field in the request it is autogenerated from backend */
  public getLineItemsList(modifiedLineItems: any) {
    const requestList: Array<any> = []; // stores the formatted line items
    let selectedLineItem: any;
    modifiedLineItems.forEach((row: any, index: any) => {
      selectedLineItem = [];
      let isNewItem = false;
      (row.cells).forEach((cell: any) => {
        if (cell.cName === 'isNewItem' && cell.cVal) {
          isNewItem = true;
        }
      });
      let typeName = '';
      if (!isNewItem) {
        typeName = 'original_';
        (row.cells).forEach((cell: any) => {
          if (cell.cName !== 'isNewItem') {
            if ((cell.cValOrig !== undefined) && (cell.cValOrig !== cell.cVal)) { // for fields which are edited
              selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cValOrig });
              selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
            } else { // for fields which are not edited
              selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cVal });
              selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
            }
          } else { // for new line items
            selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        });
      } else {
        (row.cells).forEach((cell: any) => {
          // no need to push rowId field in the request it is autogenerated from backend
          if (cell.cName !== 'rowId') {
            selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        });
      }
      requestList.push({ rIdx: index, cells: selectedLineItem });
    });
    return requestList;
  }

  // function to duplicate a selected row
  public duplicateRow() {
    let rowIndex: any;
    const selectedRows = this.dataService.detailsSearchService.getPagedData().pageRows.filter((row: any) => {
      return row.isSelected;
    });
    this.dataService.detailsSearchService.getPagedData().pageRows.forEach((row: any, index: any) => {
      if (row.isSelected === true) {
        rowIndex = index;
      }
    });
    if (selectedRows.length > 1) {
      this.alertService.clearAll().error(this.resourceService.get('msg_select_only_one'));
      return;
    } else if (selectedRows.length === 1) {
      this.dataService.detailsSearchService.addNewRowToModel();
      const newRow = this.dataService.detailsSearchService.getPagedData().pageRows[0];
      const rowToDuplicate = selectedRows[0];
      const cells = Object.keys(rowToDuplicate.cells);
      cells.forEach(element => {
        // reportLineNo field is autogenerated, no need to copy it from selected row
        if (element && element !== 'isNewItem' && element !== 'reportLineNo') {
          newRow.cells[element].cVal = rowToDuplicate.cells[element].cVal;
        }
      });
      return;
    } else if (selectedRows.length === 0) {
      this.alertService.clearAll().error(this.resourceService.get('msg_no_row_selected'));
      return;
    }
    return 0;
  }

  // Method used to delete all the Line Items

  // This method is overidden to avoid the confirmation dialog box of the line items deletion
  // during the report header deletion

  // After line items deletion's confirmation, this method calls
  // this.deleteAllLineItemsWithoutConfirmation() for the backend call
  public deleteAllLineItems() {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    if (noOfLineItems > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            this.deleteAllLineItemsWithoutConfirmation()
              .subscribe((resp: any) => {
                if (resp['statusCode'] !== undefined && resp['statusCode'] !== null) {
                  if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                    // code to reload the detail grid
                    this.alertService.clearAll();
                    this.alertService.success(this.resourceService.get(this.successMsg));
                    this.dataService.reloadDetailTable();
                  } else {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                  }
                } else {
                  if (resp.error) {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp.error));
                  }
                }
              }, (error) => {
                this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
              });
          }
        });
    } else {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
    }
  }

// Method to delete the line items without the confimation dialog box for line items deletion
  private deleteAllLineItemsWithoutConfirmation() {
    const selectedRows = this.dataService.getDetailsSearchService().getPagedData().pageRows;
    const lineItemProp = this.dataService.detailsSearchParams.idProperty;
    const newLineItemNumbers: Array<string> = [];
    let hasExistingLineItems = false;
    selectedRows.forEach((row: any) => {
      if (row.cells['isNewItem'] && row.cells['isNewItem'].cVal === true) {
        newLineItemNumbers.push(row.cells[lineItemProp].cVal);
      } else {
        hasExistingLineItems = true;
      }
    });

    if (newLineItemNumbers.length > 0) {
      this.dataService.getDetailsSearchService().deleteRowsFromModel(lineItemProp, newLineItemNumbers);
      this.buttonService.initializeButtons(this.dataService.primaryDataMap);
    }

    if (hasExistingLineItems) {
      const entityNumber = this.dataService.getPrimaryDataIdValue();
      return this.backendService.deleteAllLineItemsFromEntity(entityNumber);
    }
    return;
  }

  // Method used to delete the selected Entity

  // This method is overridden to delete all the line items of the report before deleting header
  // By calling the deleteAllLineItemsWithoutConfirmation() method after the confirmation. Then, on success of
  // line items deletion, header is deleted using headerdeletionhelper() method

  // Line items are deleted first, since if we delete only the header part, line items will not get
  // deleted due to backend code issue
  public delete() {
    const isNewItem = this.dataService.getPrimarySearchService().getDisplayMode() === 'C'
      && this.dataService.getDetailsSearchService().getDisplayMode() === 'C';
    if (isNewItem) {
      this.alertService.clearAll().success(this.resourceService.get(this.successMsg));
      this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
    } else {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
            if (noOfLineItems > 0) {
              // To delete the line items if any
              this.deleteAllLineItemsWithoutConfirmation()
                .subscribe((deleteAllLineItemResp: any) => {
                  if (deleteAllLineItemResp['statusCode'] !== undefined && deleteAllLineItemResp['statusCode'] !== null) {
                    if (deleteAllLineItemResp['statusCode'] === 200 && deleteAllLineItemResp['statusMessage'] === 'SUCCESS') {
                      // code to reload the detail grid
                      this.alertService.clearAll();
                      this.alertService.success(this.resourceService.get(this.successMsg));
                      this.dataService.reloadDetailTable();

                      /// To delete the header of the report on success of line item deletion
                      this.headerDeletionHelper();
                      //////////////////////////////////////
                    } else {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(deleteAllLineItemResp.statusMessage));
                    }
                  } else {
                    if (deleteAllLineItemResp.error) {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(deleteAllLineItemResp.error));
                    }
                  }
                }, (error) => {
                  this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
                });
            } else {
              // To delete the header alone, if no line items are present
              this.headerDeletionHelper();
              //////////////////
            }
          }
        });
    }
  }


  // This method is to delete the header part of the report
  private headerDeletionHelper() {
    const itemNumber = this.dataService.getPrimaryDataIdValue();
    this.backendService.deleteEntity(itemNumber)
      .subscribe((resp: any) => {
        if (resp['statusCode'] && resp['statusMessage']) {
          if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
            // reload the search page
            this.alertService.clearAll();
            this.alertService.success(this.resourceService.get(this.successMsg));
            this.navService.preventNavigation = false;
            this.navService.removeLastBreadCrumbItem();
            this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
          } else if (resp['statusCode'] === 500) {
            this.alertService.clearAll();
            this.alertService.error(this.resourceService.translateServerResponse(resp['statusMessage']));
          }
        } else {
          if (resp.error) {
            this.alertService.clearAll();
            this.alertService.error(this.resourceService.translateServerResponse(resp.error));
          }
        }
      }
        , (error) => {
          this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
        });
  }


  // [LFWM-3408] Function to validate reportParmName fields, checking if its value starts with PARAM_WMS_c_ prefix
  public validateLineItems(lineItems) {
    if (lineItems.length !== 0) {
      let reportParmNameFlag = false;
      let isColorFlag = false;
      let isCollateFlag=false;
      let isDuplexFlag=false;
      let isLandscapeFlag=false;
      let isPrintnextonfail=false;
      lineItems.forEach((detail: any) => {
        detail.cells.forEach(property => {
          if ((property.cName === 'reportParmName1' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName2' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName3' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName4' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName5' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName6' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName7' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName8' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName9' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName10' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName11' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName12' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName13' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName14' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName15' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName16' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName17' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName18' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName19' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
            || (property.cName === 'reportParmName20' && (property.cVal) && (!property.cVal.startsWith('PARAM_WMS_c_')))
          ) {
            reportParmNameFlag = true;
          }
          else if (property.cName === 'isColor' && !property.cVal) {
            isColorFlag = true;
          } else if (property.cName === 'isDuplex' && !property.cVal) {
            isDuplexFlag = true;
          }
          else if (property.cName === 'isCollate' && !property.cVal) {
            isCollateFlag = true;
          } else if (property.cName === 'isLandscape' && !property.cVal) {
            isLandscapeFlag = true;
          }
          else if (property.cName === 'printNextOnFail' && !property.cVal) {
            isPrintnextonfail = true;
          }
          
        });
      });
      if (reportParmNameFlag) {
        return this.resourceService.get('rpt_param_valdtion_msg');
      }
      else if (isColorFlag) {
        return this.resourceService.get('valid_IsColor');
      } else if (isDuplexFlag) {
        return this.resourceService.get('valid_IsDuplex');
      } else if (isCollateFlag) {
        return this.resourceService.get('valid_IsCollate');
      }
      else if (isLandscapeFlag) {
        return this.resourceService.get('valid_IsLandScape');
      } else if (isPrintnextonfail) {
        return this.resourceService.get('valid_PrintNextOnFail');
      }
    return null;
  }
}
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/features/reports/reports-configuration/services/reports-configuration-action.service.ts